import { forwardRef, useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { cn } from '../ui/utils';
import { parseCurrency, formatCurrency } from '../../lib/utils/format';

interface MoneyInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  /**
   * Controlled value (as number)
   */
  value?: number;
  /**
   * Change handler (receives number)
   */
  onChange?: (value: number) => void;
  /**
   * Currency code
   * @default 'BRL'
   */
  currency?: string;
  /**
   * Allow negative values
   * @default false
   */
  allowNegative?: boolean;
  /**
   * Test ID for testing
   * @default 'money-input'
   */
  testId?: string;
}

/**
 * MoneyInput - Currency input with automatic formatting
 * 
 * Formats input as currency while typing.
 * Value is controlled as a number for easy form handling.
 * 
 * @example
 * ```tsx
 * const [amount, setAmount] = useState(0);
 * 
 * <MoneyInput
 *   value={amount}
 *   onChange={setAmount}
 *   currency="BRL"
 *   placeholder="R$ 0,00"
 * />
 * ```
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(
  function MoneyInput(
    {
      value = 0,
      onChange,
      currency = 'BRL',
      allowNegative = false,
      className,
      testId = 'money-input',
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) {
    const [displayValue, setDisplayValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    // Update display value when value prop changes
    useEffect(() => {
      if (!isFocused) {
        setDisplayValue(value === 0 ? '' : formatCurrency(value, currency));
      }
    }, [value, currency, isFocused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      // Remove non-numeric characters except minus and decimal separators
      const cleaned = input.replace(/[^\d,.-]/g, '');

      // Parse to number
      let numericValue = parseCurrency(cleaned);

      // Handle negative values
      if (!allowNegative && numericValue < 0) {
        numericValue = Math.abs(numericValue);
      }

      // Update display value
      setDisplayValue(cleaned);

      // Call onChange with numeric value
      if (onChange) {
        onChange(numericValue);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Show raw value when focused
      if (value === 0) {
        setDisplayValue('');
      } else {
        setDisplayValue(value.toString().replace('.', ','));
      }
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      // Format as currency when blurred
      if (value !== 0) {
        setDisplayValue(formatCurrency(value, currency));
      }
      onBlur?.(e);
    };

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn('font-mono tabular-nums', className)}
        data-testid={testId}
        {...props}
      />
    );
  }
);
